import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent,
} from "react";
import {
  CAMPAIGN_CIRCUITS,
  getCampaignMap,
  mapForManifest,
  type MapManifest,
} from "@neon-fuse/content";
import type { GameSnapshot } from "@neon-fuse/game-core";
import type { ServerMessage } from "@neon-fuse/netcode";
import { InputController, type ControlAction } from "./game/input";
import type { GameMode } from "./game/NeonFuseGame";
import { RoomClient, type RoomStatus } from "./network/RoomClient";

type View = "home" | "battle";
type SocialProvider = "google" | "discord";
const CAMPAIGN_PROGRESS_KEY = "neon-fuse:campaign-progress";

const CONTROL_LABELS: Readonly<Record<ControlAction, string>> = Object.freeze({
  up: "Move up",
  down: "Move down",
  left: "Move left",
  right: "Move right",
  bomb: "Drop bomb",
  detonate: "Detonate remote bomb",
});

function formatTime(ticks: number): string {
  const seconds = Math.max(0, Math.ceil(ticks / 60));
  return `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
}

function readCampaignProgress(): ReadonlySet<string> {
  if (typeof window === "undefined") return new Set<string>();
  try {
    const stored = window.localStorage.getItem(CAMPAIGN_PROGRESS_KEY);
    const parsed: unknown = stored === null ? [] : JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set<string>();
    return new Set(
      parsed.filter((entry): entry is string => typeof entry === "string"),
    );
  } catch {
    return new Set<string>();
  }
}

function App(): JSX.Element {
  const [view, setView] = useState<View>("home");
  const [mode, setMode] = useState<GameMode>("battle");
  const [snapshot, setSnapshot] = useState<GameSnapshot | undefined>();
  const [roundResult, setRoundResult] = useState<GameSnapshot | undefined>();
  const [arenaName, setArenaName] = useState("Afterglow");
  const [gameReady, setGameReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [installAvailable, setInstallAvailable] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCampaign, setShowCampaign] = useState(false);
  const [showLobby, setShowLobby] = useState(false);
  const [onlineClient, setOnlineClient] = useState<RoomClient | undefined>();
  const [completedCampaignMaps, setCompletedCampaignMaps] =
    useState<ReadonlySet<string>>(readCampaignProgress);
  const [selectedMapId, setSelectedMapId] = useState(
    CAMPAIGN_CIRCUITS[0]?.stages[0]?.mapId ?? "",
  );
  const [activeMapId, setActiveMapId] = useState<string | undefined>(undefined);
  const hostRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputController | undefined>(undefined);
  const gameRef = useRef<
    import("./game/NeonFuseGame").NeonFuseGame | undefined
  >(undefined);
  const deferredInstallPrompt = useRef<BeforeInstallPromptEvent | undefined>(
    undefined,
  );
  const musicRef = useRef<HTMLAudioElement | undefined>(undefined);
  const activeMap =
    activeMapId === undefined ? undefined : getCampaignMap(activeMapId);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        CAMPAIGN_PROGRESS_KEY,
        JSON.stringify([...completedCampaignMaps]),
      );
    } catch {
      // Local progress is optional when storage is unavailable.
    }
  }, [completedCampaignMaps]);

  useEffect(() => {
    const onBeforeInstall = (event: Event): void => {
      event.preventDefault();
      deferredInstallPrompt.current = event as BeforeInstallPromptEvent;
      setInstallAvailable(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () =>
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  useEffect(() => {
    if (view !== "home" || !("serviceWorker" in navigator)) return;
    void navigator.serviceWorker.ready.then(async (registration) => {
      await registration.update();
      if (registration.waiting !== null) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        window.location.reload();
      }
    });
  }, [view]);

  useEffect(() => {
    if (view !== "battle" || hostRef.current === null) return;
    const input = new InputController();
    input.attach();
    inputRef.current = input;
    setGameReady(false);
    let disposed = false;
    let runtime: import("./game/NeonFuseGame").NeonFuseGame | undefined;
    void import("./game/NeonFuseGame").then(({ NeonFuseGame }) => {
      if (disposed || hostRef.current === null) return;
      runtime = new NeonFuseGame({
        parent: hostRef.current,
        mode,
        reducedMotion,
        input,
        ...(activeMap === undefined
          ? {}
          : {
              arenaMap: mapForManifest(activeMap),
              arenaName: activeMap.name,
            }),
        ...(onlineClient === undefined ? {} : { roomClient: onlineClient }),
        onHudSnapshot: setSnapshot,
        onRoundEnd: (result) => {
          setRoundResult(result);
          if (
            activeMap !== undefined &&
            result.status === "won" &&
            result.winnerId === "p1"
          ) {
            setCompletedCampaignMaps((current) => {
              if (current.has(activeMap.id)) return current;
              const next = new Set(current);
              next.add(activeMap.id);
              return next;
            });
          }
        },
        onArenaName: setArenaName,
        onReady: () => setGameReady(true),
      });
      gameRef.current = runtime;
    });
    return () => {
      disposed = true;
      runtime?.destroy();
      input.detach();
      inputRef.current = undefined;
      gameRef.current = undefined;
    };
  }, [activeMap, mode, onlineClient, reducedMotion, view]);

  useEffect(() => {
    gameRef.current?.setAudioEnabled(audioEnabled);
    if (view !== "battle" || !audioEnabled) {
      musicRef.current?.pause();
      musicRef.current = undefined;
      return;
    }
    const music = new Audio("/media/battle-loop.mp3");
    music.loop = true;
    music.volume = 0.16;
    musicRef.current = music;
    void music.play().catch(() => undefined);
    return () => {
      music.pause();
      music.currentTime = 0;
      if (musicRef.current === music) musicRef.current = undefined;
    };
  }, [audioEnabled, view]);

  const openGame = (nextMode: GameMode, mapId?: string): void => {
    const nextMap = mapId === undefined ? undefined : getCampaignMap(mapId);
    if (mapId !== undefined && nextMap === undefined) return;
    onlineClient?.close();
    setOnlineClient(undefined);
    setMode(nextMode);
    setActiveMapId(nextMap?.id);
    setArenaName(nextMap?.name ?? "Afterglow");
    setRoundResult(undefined);
    setSnapshot(undefined);
    setGameReady(false);
    setShowCampaign(false);
    setView("battle");
  };

  const openOnlineGame = (client: RoomClient): void => {
    setOnlineClient(client);
    setMode("online");
    setActiveMapId(undefined);
    setArenaName("Afterglow");
    setRoundResult(undefined);
    setSnapshot(undefined);
    setGameReady(false);
    setShowLobby(false);
    setView("battle");
  };

  const leaveGame = (): void => {
    gameRef.current?.destroy();
    onlineClient?.close();
    setOnlineClient(undefined);
    setActiveMapId(undefined);
    setView("home");
    setRoundResult(undefined);
  };

  const restartGame = (): void => {
    if (mode === "online") return;
    setRoundResult(undefined);
    gameRef.current?.restart();
  };

  const pressControl = (action: ControlAction, pressed: boolean): void => {
    inputRef.current?.set(action, pressed);
  };

  const install = async (): Promise<void> => {
    const prompt = deferredInstallPrompt.current;
    if (prompt === undefined) return;
    await prompt.prompt();
    deferredInstallPrompt.current = undefined;
    setInstallAvailable(false);
  };

  if (view === "home") {
    return (
      <main className="app-shell home-shell">
        <div className="ambient ambient-one" />
        <div className="ambient ambient-two" />
        <header className="topbar">
          <button
            className="brand-button"
            type="button"
            onClick={() => setShowHelp(false)}
            aria-label="Neon Fuse home"
          >
            <span className="brand-mark" aria-hidden="true">
              <span />
            </span>
            <span className="brand-word">
              NEON<span>FUSE</span>
            </span>
          </button>
          <div className="topbar-actions">
            <span className="version-tag">BUILD 0.1 / LOCAL ARCADE</span>
            <button
              className="online-button"
              type="button"
              onClick={() => {
                setShowHelp(false);
                setShowCampaign(false);
                setShowLobby(true);
              }}
              aria-label="Open online lobby"
            >
              <span className="live-dot" aria-hidden="true" /> ONLINE
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={() => setShowHelp((visible) => !visible)}
              aria-label="How to play"
            >
              ?
            </button>
          </div>
        </header>

        <section className="hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="live-dot" /> SEASON 01 / CIRCUIT: AFTERGLOW
            </p>
            <h1>
              DROP.
              <br />
              <em>DASH.</em>
              <br />
              DETONATE.
            </h1>
            <p className="hero-lede">
              A precision arcade arena where every fuse is a decision and the
              best escape is the one you planned two seconds ago.
            </p>
            <div className="hero-actions">
              <button
                className="primary-button"
                type="button"
                onClick={() => openGame("battle")}
              >
                <span>Enter the arena</span>
                <strong>→</strong>
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => openGame("attract")}
              >
                Watch attract loop <span>◌</span>
              </button>
            </div>
            <div className="keyline-stats">
              <div>
                <strong>60</strong>
                <span>tick simulation</span>
              </div>
              <div>
                <strong>08</strong>
                <span>player arena</span>
              </div>
              <div>
                <strong>∞</strong>
                <span>chain reactions</span>
              </div>
            </div>
          </div>
          <div className="hero-art" aria-label="A neon arena preview">
            <div className="hero-art-glow" />
            <div className="hero-ring ring-one" />
            <div className="hero-ring ring-two" />
            <div className="preview-board">
              {Array.from({ length: 35 }, (_, index) => (
                <span
                  className={
                    index % 7 === 0 || index % 11 === 0
                      ? "preview-wall"
                      : "preview-floor"
                  }
                  key={index}
                />
              ))}
              <div className="preview-bomb">
                <i />
              </div>
              <div className="preview-avatar">
                <span />
              </div>
              <div className="preview-flare flare-one" />
              <div className="preview-flare flare-two" />
            </div>
            <div className="hero-caption">
              <span>LIVE SYSTEM</span>
              <strong>AFTERGLOW / 01</strong>
            </div>
          </div>
        </section>

        <section className="mode-deck">
          <div className="section-heading">
            <div>
              <span className="section-index">01</span>
              <h2>Choose your circuit</h2>
            </div>
            <span>QUICK START / NO ACCOUNT REQUIRED</span>
          </div>
          <div className="mode-grid">
            <button
              className="mode-card mode-card-main"
              type="button"
              onClick={() => openGame("battle")}
            >
              <span className="mode-number">A01</span>
              <span className="mode-icon">✦</span>
              <strong>Quick Battle</strong>
              <p>One arena. One rival. Make the first spark count.</p>
              <span className="mode-cta">
                PLAY NOW <b>↗</b>
              </span>
            </button>
            <button
              className="mode-card"
              type="button"
              onClick={() => openGame("attract")}
            >
              <span className="mode-number">A02</span>
              <span className="mode-icon mode-icon-cyan">◈</span>
              <strong>Attract Loop</strong>
              <p>Let the bots show you what the arena can do.</p>
              <span className="mode-cta">
                WATCH <b>↗</b>
              </span>
            </button>
            <button
              className="mode-card mode-card-campaign"
              type="button"
              onClick={() => {
                setShowHelp(false);
                setShowCampaign(true);
              }}
            >
              <span className="mode-number">A03</span>
              <span className="mode-icon mode-icon-amber">⌁</span>
              <strong>Campaign</strong>
              <p>Five circuits. Forty authored routes. Find the clean line.</p>
              <span className="mode-cta">
                OPEN CAMPAIGN <b>↗</b>
              </span>
            </button>
          </div>
        </section>

        <section className="info-strip">
          <div>
            <span className="section-index">02</span>
            <strong>Built for the big screen. Tuned for the thumb.</strong>
            <p>
              Keyboard, gamepad, touch, portrait, landscape, and offline local
              play.
            </p>
          </div>
          <div className="info-actions">
            <button
              className="text-button"
              type="button"
              onClick={() => setShowHelp(true)}
            >
              Controls & accessibility <span>↗</span>
            </button>
            {installAvailable && (
              <button
                className="install-button"
                type="button"
                onClick={() => void install()}
              >
                Install PWA
              </button>
            )}
          </div>
        </section>

        {showCampaign && (
          <CampaignPanel
            selectedMapId={selectedMapId}
            completedMapIds={completedCampaignMaps}
            onSelect={setSelectedMapId}
            onStart={(mapId) => openGame("battle", mapId)}
            onClose={() => setShowCampaign(false)}
          />
        )}
        {showLobby && (
          <OnlinePanel
            onStart={openOnlineGame}
            onClose={() => setShowLobby(false)}
          />
        )}
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
        <footer className="footer">
          <span>NEON FUSE / ORIGINAL ARCADE SYSTEM</span>
          <span>NO LOOT BOXES. JUST GOOD TIMING.</span>
        </footer>
      </main>
    );
  }

  const localPlayerId = onlineClient?.assignedPlayerId ?? "p1";
  const player = snapshot?.players.find(
    (candidate) => candidate.id === localPlayerId,
  );
  const rival = snapshot?.players.find(
    (candidate) => candidate.id !== localPlayerId,
  );
  const statusText =
    activeMap !== undefined
      ? `CAMPAIGN / ${activeMap.circuitId.toUpperCase()}`
      : mode === "attract"
        ? "AUTOMATED SHOWCASE / FOUR BOTS"
        : mode === "online"
          ? `ONLINE ROOM / SLOT ${localPlayerId.toUpperCase()}`
          : "LOCAL BATTLE / FOUR PLAYER SLOTS";
  const resultText =
    roundResult?.status === "draw"
      ? "DOUBLE DETONATION"
      : roundResult?.winnerId === localPlayerId
        ? "ARENA CLEARED"
        : mode === "online"
          ? "ANOTHER SLOT TAKES THE CIRCUIT"
          : "NOVA TAKES THE CIRCUIT";

  return (
    <main className="app-shell battle-shell">
      <header className="battle-topbar">
        <button
          className="brand-button"
          type="button"
          onClick={leaveGame}
          aria-label="Return home"
        >
          <span className="brand-mark" aria-hidden="true">
            <span />
          </span>
          <span className="brand-word">
            NEON<span>FUSE</span>
          </span>
        </button>
        <div className="battle-meta">
          <span className="live-dot" /> {statusText}
          <span className="meta-divider" />{" "}
          <span>ARENA 01 / {arenaName.toUpperCase()}</span>
        </div>
        <div className="battle-actions">
          <button
            className="compact-button"
            type="button"
            onClick={() => setReducedMotion((value) => !value)}
          >
            {reducedMotion ? "FX: LOW" : "FX: HIGH"}
          </button>
          <button
            className="compact-button"
            type="button"
            onClick={() => setAudioEnabled((value) => !value)}
          >
            {audioEnabled ? "SOUND ON" : "SOUND OFF"}
          </button>
          <button
            className="icon-button"
            type="button"
            onClick={leaveGame}
            aria-label="Exit match"
          >
            ×
          </button>
        </div>
      </header>
      <section className="battle-layout">
        <div className="arena-panel">
          <div className="arena-topline">
            <span>
              <b className="status-chip">LIVE</b>{" "}
              <span className="muted-label">MATCH FEED</span>
            </span>
            <span className="arena-timer">
              {formatTime(snapshot?.roundTicksRemaining ?? 7200)}
            </span>
            <span className="muted-label">
              {arenaName.toUpperCase()} / TICK{" "}
              {snapshot?.tick.toString().padStart(5, "0") ?? "00000"}
            </span>
          </div>
          <div
            className={`game-canvas${gameReady ? " ready" : ""}`}
            ref={hostRef}
            aria-label="Neon Fuse game arena"
            aria-busy={!gameReady}
          />
          <div className="arena-bottomline">
            <span>
              <i className="signal-bars" /> SERVER READY / PREDICTION LOCKED
            </span>
            <span>
              WASD / ARROWS TO MOVE <b>SPACE</b> TO DROP
            </span>
          </div>
          <TouchControls onControl={pressControl} />
          {roundResult !== undefined && (
            <div className="result-card">
              <span className="result-kicker">
                ROUND COMPLETE / SEED {roundResult.seed}
              </span>
              <h2>{resultText}</h2>
              <p>
                {roundResult.status === "draw"
                  ? "Nobody outran the final ring."
                  : roundResult.winnerId === localPlayerId
                    ? "Your route through the blast was immaculate."
                    : mode === "online"
                      ? "Another player found the better exit this time."
                      : "The bot found the better exit this time."}
              </p>
              <div className="result-actions">
                <button
                  className="primary-button"
                  type="button"
                  onClick={restartGame}
                >
                  <span>Run it back</span>
                  <strong>↻</strong>
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={leaveGame}
                >
                  Return to deck
                </button>
              </div>
            </div>
          )}
        </div>
        <aside className="match-rail">
          <div className="rail-header">
            <span className="section-index">03</span>
            <div>
              <span className="muted-label">MATCH CONTROL</span>
              <h2>Blast report</h2>
            </div>
          </div>
          <div className="player-card player-card-you">
            <div className="player-heading">
              <span className="player-swatch swatch-coral" />
              <strong>YOU</strong>
              <span className="player-state">
                {player?.alive === false ? "OUT" : "ACTIVE"}
              </span>
            </div>
            <div className="player-values">
              <div>
                <span>RANGE</span>
                <strong>{player?.bombRange ?? 2}</strong>
              </div>
              <div>
                <span>BOMBS</span>
                <strong>
                  {player?.bombsAvailable ?? 1}
                  <small>/{player?.maxBombs ?? 1}</small>
                </strong>
              </div>
              <div>
                <span>SPEED</span>
                <strong>
                  {player?.speedLevel ?? 0}
                  <small>/5</small>
                </strong>
              </div>
            </div>
            <div className="meter">
              <span
                style={{
                  width: `${Math.min(100, ((player?.bombRange ?? 2) / 8) * 100)}%`,
                }}
              />
            </div>
          </div>
          <div className="player-card player-card-rival">
            <div className="player-heading">
              <span className="player-swatch swatch-cyan" />
              <strong>
                {mode === "online" ? (
                  (rival?.name ?? rival?.id ?? "RIVAL").toUpperCase()
                ) : (
                  <>
                    NOVA <em>BOT</em>
                  </>
                )}
              </strong>
              <span className="player-state">
                {rival?.alive === false ? "OUT" : "HUNTING"}
              </span>
            </div>
            <div className="rival-note">
              <span className="bot-orbit">◌</span>
              <div>
                <strong>
                  {mode === "online" ? "REMOTE PLAYER" : "OPPORTUNIST"}
                </strong>
                <p>
                  {mode === "online"
                    ? "Authoritative room snapshot."
                    : "Reads the next 120 ticks."}
                </p>
              </div>
            </div>
          </div>
          <div className="rail-section">
            <div className="rail-section-heading">
              <span>POWER SYSTEMS</span>
              <span>{player?.score ?? 0} SCORE</span>
            </div>
            <div className="power-list">
              <PowerRow
                label="BOMB CAPACITY"
                value={player?.maxBombs ?? 1}
                max={8}
                color="coral"
              />
              <PowerRow
                label="BLAST RANGE"
                value={player?.bombRange ?? 2}
                max={8}
                color="amber"
              />
              <PowerRow
                label="MOBILITY"
                value={(player?.speedLevel ?? 0) + 1}
                max={6}
                color="cyan"
              />
            </div>
          </div>
          <div className="rail-tip">
            <span className="tip-mark">!</span>
            <div>
              <strong>READ THE FUSE</strong>
              <p>
                Chain reactions are faster than panic. Find the exit before you
                place the next bomb.
              </p>
            </div>
          </div>
          <button className="restart-link" type="button" onClick={restartGame}>
            RESET ARENA <span>↻</span>
          </button>
        </aside>
      </section>
    </main>
  );
}

function PowerRow({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}): JSX.Element {
  return (
    <div className="power-row">
      <div>
        <span>{label}</span>
        <strong>{value.toString().padStart(2, "0")}</strong>
      </div>
      <div className="power-dots">
        {Array.from({ length: max }, (_, index) => (
          <i className={index < value ? `filled ${color}` : ""} key={index} />
        ))}
      </div>
    </div>
  );
}

function TouchControls({
  onControl,
}: {
  onControl: (action: ControlAction, pressed: boolean) => void;
}): JSX.Element {
  const press =
    (action: ControlAction) =>
    (event: PointerEvent<HTMLButtonElement>): void => {
      event.currentTarget.setPointerCapture(event.pointerId);
      onControl(action, true);
    };
  const release = (action: ControlAction) => (): void =>
    onControl(action, false);
  return (
    <div className="touch-controls" aria-label="Touch controls">
      {(["up", "left", "down", "right"] as const).map((action) => (
        <button
          key={action}
          className={`touch-key touch-${action}`}
          type="button"
          aria-label={CONTROL_LABELS[action]}
          onPointerDown={press(action)}
          onPointerUp={release(action)}
          onPointerCancel={release(action)}
          onPointerLeave={release(action)}
        >
          {action === "up"
            ? "↑"
            : action === "down"
              ? "↓"
              : action === "left"
                ? "←"
                : "→"}
        </button>
      ))}
      <button
        className="touch-key touch-bomb"
        type="button"
        aria-label={CONTROL_LABELS.bomb}
        onPointerDown={press("bomb")}
        onPointerUp={release("bomb")}
        onPointerCancel={release("bomb")}
        onPointerLeave={release("bomb")}
      >
        DROP
      </button>
    </div>
  );
}

function CampaignPanel({
  selectedMapId,
  completedMapIds,
  onSelect,
  onStart,
  onClose,
}: {
  selectedMapId: string;
  completedMapIds: ReadonlySet<string>;
  onSelect: (mapId: string) => void;
  onStart: (mapId: string) => void;
  onClose: () => void;
}): JSX.Element {
  const initialCircuit =
    CAMPAIGN_CIRCUITS.find((circuit) =>
      circuit.stages.some((stage) => stage.mapId === selectedMapId),
    ) ?? CAMPAIGN_CIRCUITS[0];
  const [circuitId, setCircuitId] = useState(initialCircuit?.id ?? "");
  const circuit = CAMPAIGN_CIRCUITS.find(
    (candidate) => candidate.id === circuitId,
  );
  const selectedMap = getCampaignMap(selectedMapId);
  const routeCount = CAMPAIGN_CIRCUITS.reduce(
    (total, candidate) => total + candidate.stages.length,
    0,
  );
  const clearedCount = [...completedMapIds].filter(
    (mapId) => getCampaignMap(mapId) !== undefined,
  ).length;

  const selectCircuit = (nextCircuitId: string): void => {
    setCircuitId(nextCircuitId);
    const nextCircuit = CAMPAIGN_CIRCUITS.find(
      (candidate) => candidate.id === nextCircuitId,
    );
    const firstStage = nextCircuit?.stages[0];
    if (firstStage !== undefined) onSelect(firstStage.mapId);
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="campaign-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="panel-heading">
          <div>
            <span className="section-index">
              CAMPAIGN / {clearedCount.toString().padStart(2, "0")} OF{" "}
              {routeCount} CLEARED
            </span>
            <h2 id="campaign-title">The Luminous Run</h2>
            <p className="campaign-lede">
              Five circuits. Eight stages each. Every final route has a signal
              to break.
            </p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close campaign"
          >
            ×
          </button>
        </div>
        <div className="campaign-layout">
          <nav className="campaign-circuits" aria-label="Campaign circuits">
            <span className="help-key">SELECT CIRCUIT</span>
            {CAMPAIGN_CIRCUITS.map((candidate, index) => (
              <button
                className={`campaign-circuit${
                  candidate.id === circuitId ? " selected" : ""
                }`}
                type="button"
                aria-pressed={candidate.id === circuitId}
                onClick={() => selectCircuit(candidate.id)}
                key={candidate.id}
              >
                <span>{`0${index + 1}`}</span>
                <strong>{candidate.name}</strong>
                <small>
                  {
                    candidate.stages.filter((stage) =>
                      completedMapIds.has(stage.mapId),
                    ).length
                  }
                  /{candidate.stages.length} CLEARED
                </small>
              </button>
            ))}
          </nav>
          <div className="campaign-stage-pane">
            <div className="campaign-stage-heading">
              <div>
                <span className="help-key">{circuit?.name ?? "CIRCUIT"}</span>
                <h3>{circuit?.tagline ?? "Choose a route."}</h3>
              </div>
              <span className="campaign-route-count">
                {circuit?.stages.length ?? 0} ROUTES
              </span>
            </div>
            <div className="campaign-stage-grid">
              {(circuit?.stages ?? []).map((stage) => {
                const map = getCampaignMap(stage.mapId);
                if (map === undefined) return null;
                const selected = map.id === selectedMapId;
                const completed = completedMapIds.has(map.id);
                return (
                  <button
                    className={`campaign-stage${selected ? " selected" : ""}${
                      completed ? " completed" : ""
                    }`}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSelect(map.id)}
                    key={map.id}
                  >
                    <span className="campaign-stage-number">
                      STAGE {map.stage.toString().padStart(2, "0")}
                    </span>
                    <strong>{map.name}</strong>
                    <p>{map.tagline}</p>
                    <span className="campaign-stage-meta">
                      {formatCampaignLabel(map.objective)} / THREAT{" "}
                      {map.difficulty}
                    </span>
                    {completed && (
                      <span className="campaign-complete">CLEARED</span>
                    )}
                    {map.boss && (
                      <span className="campaign-boss">FINAL SIGNAL</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="campaign-launch">
              <div>
                <span className="help-key">SELECTED ROUTE</span>
                <strong>{selectedMap?.name ?? "Choose a stage"}</strong>
              </div>
              <button
                className="primary-button"
                type="button"
                disabled={selectedMap === undefined}
                onClick={() => {
                  if (selectedMap !== undefined) onStart(selectedMap.id);
                }}
              >
                <span>Launch stage</span>
                <strong>→</strong>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function OnlinePanel({
  onStart,
  onClose,
}: {
  onStart: (client: RoomClient) => void;
  onClose: () => void;
}): JSX.Element {
  const [roomId, setRoomId] = useState("afterglow");
  const [displayName, setDisplayName] = useState("FLARE");
  const [status, setStatus] = useState<RoomStatus>("idle");
  const [lastMessage, setLastMessage] = useState<ServerMessage | undefined>();
  const [assignedPlayerId, setAssignedPlayerId] = useState<
    string | undefined
  >();
  const [socialProviders, setSocialProviders] = useState<
    readonly SocialProvider[]
  >([]);
  const [error, setError] = useState<string | undefined>();
  const clientRef = useRef<RoomClient | undefined>(undefined);
  const handedOffRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!handedOffRef.current) clientRef.current?.close();
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    const configuredBase = import.meta.env.VITE_API_BASE_URL;
    const base =
      configuredBase === undefined || configuredBase.length === 0
        ? window.location.origin
        : configuredBase;
    void fetch(new URL("/api/auth-config", base))
      .then(async (response) => {
        if (!response.ok) return;
        const payload: unknown = await response.json();
        if (
          disposed ||
          typeof payload !== "object" ||
          payload === null ||
          !("socialProviders" in payload) ||
          !Array.isArray(payload.socialProviders)
        ) {
          return;
        }
        setSocialProviders(payload.socialProviders.filter(isSocialProvider));
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
    };
  }, []);

  const connect = (): void => {
    const nextRoomId = roomId.trim();
    const nextDisplayName = displayName.trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(nextRoomId)) {
      setError("Room codes use letters, numbers, underscores, or hyphens.");
      return;
    }
    if (nextDisplayName.length < 1 || nextDisplayName.length > 48) {
      setError("Display names must be between 1 and 48 characters.");
      return;
    }

    clientRef.current?.close();
    setError(undefined);
    setLastMessage(undefined);
    setAssignedPlayerId(undefined);
    try {
      const client = new RoomClient({
        roomId: nextRoomId,
        displayName: nextDisplayName,
        onStatus: (nextStatus) => {
          if (!handedOffRef.current) setStatus(nextStatus);
        },
        onMessage: (message) => {
          if (handedOffRef.current) return;
          setLastMessage(message);
          if (
            message.type === "joined" ||
            message.type === "reconnect-accepted"
          ) {
            setAssignedPlayerId(message.playerId);
          }
        },
      });
      clientRef.current = client;
      client.connect();
    } catch (cause) {
      setStatus("error");
      setError(cause instanceof Error ? cause.message : "Unable to open room");
    }
  };

  const disconnect = (): void => {
    clientRef.current?.close();
    clientRef.current = undefined;
    setAssignedPlayerId(undefined);
  };

  const signInSocial = async (provider: SocialProvider): Promise<void> => {
    setError(undefined);
    const configuredBase = import.meta.env.VITE_API_BASE_URL;
    const base =
      configuredBase === undefined || configuredBase.length === 0
        ? window.location.origin
        : configuredBase;
    try {
      const response = await fetch(new URL("/api/auth/sign-in/social", base), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider,
          callbackURL: window.location.origin,
        }),
      });
      const payload: unknown = await response.json();
      const redirectURL =
        typeof payload === "object" &&
        payload !== null &&
        "url" in payload &&
        typeof payload.url === "string"
          ? payload.url
          : undefined;
      if (!response.ok || redirectURL === undefined) {
        setError(
          `${socialProviderLabel(provider)} is not available right now.`,
        );
        return;
      }
      window.location.assign(redirectURL);
    } catch {
      setError("The authentication service is unavailable.");
    }
  };

  const statusTone =
    status === "connected"
      ? " online-status-good"
      : status === "error"
        ? " online-status-error"
        : "";
  const joined = status === "connected" && assignedPlayerId !== undefined;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="online-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="online-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="panel-heading">
          <div>
            <span className="section-index">ONLINE / ROOM LINK</span>
            <h2 id="online-title">Open a room. Bring the sparks.</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close online lobby"
          >
            ×
          </button>
        </div>
        <p className="online-lede">
          Join a server-authoritative room with the same deterministic rules as
          local play. Share the room code with your crew.
        </p>
        <form
          className="online-form"
          onSubmit={(event) => {
            event.preventDefault();
            connect();
          }}
        >
          <label>
            <span>ROOM CODE</span>
            <input
              value={roomId}
              maxLength={64}
              pattern={"[A-Za-z0-9_\\-]+"}
              onChange={(event) => setRoomId(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label>
            <span>DISPLAY NAME</span>
            <input
              value={displayName}
              maxLength={48}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="nickname"
            />
          </label>
          <div className="online-actions">
            {joined ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  const client = clientRef.current;
                  if (client?.assignedPlayerId === undefined) return;
                  handedOffRef.current = true;
                  onStart(client);
                }}
              >
                <span>Enter arena</span>
                <strong>→</strong>
              </button>
            ) : status === "connected" ? (
              <button
                className="secondary-button"
                type="button"
                onClick={disconnect}
              >
                Leave room
              </button>
            ) : (
              <button
                className="primary-button"
                type="submit"
                disabled={status === "connecting" || status === "reconnecting"}
              >
                <span>
                  {status === "connecting" || status === "reconnecting"
                    ? "Connecting"
                    : "Join room"}
                </span>
                <strong>→</strong>
              </button>
            )}
          </div>
        </form>
        {socialProviders.length > 0 && (
          <div className="social-login">
            <span className="help-key">CONTINUE WITH AN ACCOUNT</span>
            <div className="social-provider-grid">
              {socialProviders.map((provider) => (
                <button
                  className="secondary-button social-provider"
                  type="button"
                  onClick={() => void signInSocial(provider)}
                  key={provider}
                >
                  {socialProviderLabel(provider)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div
          className={`online-status${statusTone}`}
          role="status"
          aria-live="polite"
        >
          <span className="online-status-dot" />
          <div>
            <strong>{formatRoomStatus(status)}</strong>
            <p>{error ?? describeRoomMessage(lastMessage)}</p>
          </div>
        </div>
        <div className="online-note">
          <strong>GUEST LANE</strong>
          <span>
            Room handshakes remain available without an account. OAuth providers
            appear here when their server credentials are configured.
          </span>
        </div>
        <p className="online-footnote">
          Hosted builds use <code>VITE_API_BASE_URL</code> for the Worker room
          endpoint. Local-only play remains available when the room service is
          offline.
        </p>
      </section>
    </div>
  );
}

function formatRoomStatus(status: RoomStatus): string {
  return status.replaceAll("-", " ").toUpperCase();
}

function describeRoomMessage(message: ServerMessage | undefined): string {
  if (message === undefined) return "Ready for a room handshake.";
  switch (message.type) {
    case "hello":
      return `Room ready / ${message.maxPlayers} slots / ${message.tickRate} Hz.`;
    case "joined":
      return `Joined as ${message.playerId.toUpperCase()} / reconnect window armed.`;
    case "reconnect-accepted":
      return `Reconnected as ${message.playerId.toUpperCase()} / state reconciled.`;
    case "snapshot":
      return `Authoritative snapshot / tick ${message.serverTick}.`;
    case "error":
      return `${message.code}: ${message.message}`;
    case "disconnect":
      return message.message;
  }
}

function isSocialProvider(value: unknown): value is SocialProvider {
  return value === "google" || value === "discord";
}

function socialProviderLabel(provider: SocialProvider): string {
  return provider === "google"
    ? "Continue with Google"
    : "Continue with Discord";
}

function formatCampaignLabel(value: MapManifest["objective"]): string {
  return value.replaceAll("-", " ").toUpperCase();
}

function HelpPanel({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="help-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="panel-heading">
          <div>
            <span className="section-index">HOW TO PLAY</span>
            <h2 id="help-title">Make space. Then make sparks.</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close help"
          >
            ×
          </button>
        </div>
        <div className="help-grid">
          <div>
            <span className="help-key">WASD / ARROWS</span>
            <p>
              Move with tile-true precision. Turn early when you want to slip
              around a corner.
            </p>
          </div>
          <div>
            <span className="help-key">SPACE</span>
            <p>
              Drop a bomb. It will clear the first breakable wall in each
              direction.
            </p>
          </div>
          <div>
            <span className="help-key">E</span>
            <p>Detonate remote bombs after collecting a pink relay module.</p>
          </div>
          <div>
            <span className="help-key">TOUCH / GAMEPAD</span>
            <p>
              The arena adapts to the way you play. Controls remain reachable in
              landscape safe areas.
            </p>
          </div>
        </div>
        <div className="accessibility-note">
          <strong>ACCESSIBILITY</strong>
          <span>
            Use FX: LOW for reduced motion, high-contrast effects, and a quieter
            particle field.
          </span>
        </div>
      </section>
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export default App;
